import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOutsideClick } from 'hooks/useOutsideClick';

import { AutocompleteState, AutocompleteActionMap } from '../types';

interface AutocompleteProps {
    onOpen?: () => void;
    onClose?: () => void;
    onChange?: () => void;
    onInit?: () => void;
    onResize?: () => void;
    onReset?: () => void; // set default back
    onClear?: () => void; // clear the input and the list
}

export const useAutocomplete = <ResultEntry, Value>(
    state: AutocompleteState<ResultEntry, Value>,
    actionState: AutocompleteActionMap<ResultEntry, Value>,
    callbacks: AutocompleteProps = {}
) => {
    const [toggle, setToggle] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const { setResults, setValue, reset, clear, config } = actionState;
    const action = useMemo(
        () => ({ setResults, setValue, config }),
        [config, setResults, setValue]
    );

    const onSearch = useCallback(
        async (value: string) => {
            setInputValue(value);
            callbacks.onChange?.();
            if (value.length >= state.config.threshold) {
                const result = await state.config.searchResultProvider(value);
                if (result) {
                    callbacks.onOpen?.();
                    return result;
                }
            }
            if (value.length === 0) {
                return null;
            }
            return;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [callbacks.onOpen, callbacks.onChange, state.config]
    );

    const onToggle = useCallback(
        (isActive = false) => {
            setToggle(isActive);
            if (isActive) {
                callbacks.onOpen?.();
            } else {
                callbacks.onClose?.();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [callbacks.onOpen, callbacks.onClose]
    );

    const onReset = useCallback(
        (resetValue: Set<Value>) => {
            reset(resetValue);
            callbacks.onReset?.();
        },

        [callbacks.onReset, reset] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const onClear = useCallback(() => {
        clear();
        setInputValue('');
        callbacks.onClear?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callbacks.onClear, clear]);

    const autocompleteWrapperRef = useOutsideClick<HTMLElement>(() =>
        onToggle(false)
    );

    useEffect(() => {
        // on window-resize
        const onResize = () => {
            callbacks.onResize?.();
        };
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callbacks.onResize]);

    useEffect(() => {
        // onInit
        callbacks.onInit?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callbacks.onInit]);

    return {
        state,
        action,
        toggle,
        inputValue,
        setInputValue,
        autocompleteWrapperRef,
        onToggle,
        onSearch,
        onReset,
        onClear,
    };
};
